import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, RotateCcw, Send, Settings } from "lucide-react";
import { useState } from "react";

import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "../account/components";
import { api, formatDate } from "../api";

type DeliveryStatus = "SENDING" | "DELIVERED" | "SUPPRESSED" | "DEAD_LETTER";

type MailAdministration = {
  configuration: {
    enabled: boolean;
    fromAddress: string;
    fromName: string;
    startTlsEnabled: boolean;
    startTlsRequired: boolean;
    maximumAttempts: number;
    retryBaseDelay: string;
    recipientBudgetPermits: number;
    recipientBudgetWindow: string;
    globalBudgetPermits: number;
    globalBudgetWindow: string;
  };
  counts: Record<"total" | "sending" | "delivered" | "suppressed" | "deadLetter", number>;
  deliveries: Array<{
    id: string;
    eventType: string;
    templateKey: string;
    recipientHint: string;
    status: DeliveryStatus;
    attemptCount: number;
    lastErrorCode: string | null;
    startedAt: string;
    updatedAt: string;
    nextAttemptAt: string | null;
    retryable: boolean;
  }>;
};

const labels: Record<DeliveryStatus, string> = {
  SENDING: "发送中 / 等待重试",
  DELIVERED: "已投递",
  SUPPRESSED: "已抑制",
  DEAD_LETTER: "死信",
};

export function MailAdministrationPage() {
  const client = useQueryClient();
  const [status, setStatus] = useState<"ALL" | DeliveryStatus>("ALL");
  const [testRecipient, setTestRecipient] = useState("");
  const state = useQuery({
    queryKey: ["operator-mail-administration", status],
    queryFn: () => api<MailAdministration>(`/api/v1/operator/super-admin/mail?status=${status}&limit=50`),
  });
  const retry = useMutation({
    mutationFn: (deliveryId: string) => api<void>(`/api/v1/operator/super-admin/mail/deliveries/${deliveryId}/retry`, { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["operator-mail-administration"] }),
  });
  const testMail = useMutation({
    mutationFn: () => api<{ status: string; deliveryId: string; sentAt: string }>("/api/v1/operator/super-admin/mail/test", {
      method: "POST",
      body: JSON.stringify({ recipient: testRecipient }),
    }),
  });
  const configuration = state.data?.configuration;

  return <>
    <PageHeader eyebrow="SUPER ADMIN" title="邮件投递" description="查看验证、找回、邀请和隐私导出邮件的投递状态；完整收件地址、正文与令牌不会在此显示。" action={<StatusBadge tone={configuration?.enabled ? "good" : "warning"}><Mail />{configuration?.enabled ? "投递已启用" : "投递已停用"}</StatusBadge>} />
    {state.isPending && <LoadingState label="正在读取邮件投递状态" />}
    {state.error && <ErrorState error={state.error} retry={() => void state.refetch()} />}
    {configuration && <Section title="发送配置" description="SMTP 主机、账号与密码在系统设置中维护；保存系统设置会重启服务。" action={<a className="secondary-button" href="/operator/settings"><Settings />系统设置</a>}>
      <div className="mail-configuration-grid">
        <div><span>发件人</span><strong>{configuration.fromName}</strong><small>{configuration.fromAddress}</small></div>
        <div><span>传输安全</span><strong>{configuration.startTlsRequired ? "强制 STARTTLS" : configuration.startTlsEnabled ? "启用 STARTTLS" : "未启用 STARTTLS"}</strong><small>{configuration.startTlsRequired ? "TLS 升级失败时拒绝发送" : "请按 SMTP 服务要求配置"}</small></div>
        <div><span>失败重试</span><strong>最多 {configuration.maximumAttempts} 次</strong><small>基础退避 {configuration.retryBaseDelay}</small></div>
        <div><span>发送预算</span><strong>{configuration.globalBudgetPermits} / {configuration.globalBudgetWindow}</strong><small>每个收件人 {configuration.recipientBudgetPermits} / {configuration.recipientBudgetWindow}</small></div>
      </div>
    </Section>}
    {configuration && <Section title="发送测试邮件" description="使用当前 SMTP 配置同步发送固定诊断邮件；测试不会写入业务投递历史。">
      <form className="mail-test-form" onSubmit={(event) => { event.preventDefault(); testMail.mutate(); }}>
        <label><span>收件邮箱</span><input required type="email" maxLength={254} autoComplete="email" placeholder="admin@example.com" value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} /></label>
        <button className="primary-button" disabled={!configuration.enabled || testMail.isPending} type="submit"><Send />{testMail.isPending ? "正在发送" : "发送测试邮件"}</button>
      </form>
      <MutationMessage error={testMail.error} success={testMail.isSuccess && `测试邮件已发送 · ${formatDate(testMail.data.sentAt)}`} />
    </Section>}
    {state.data && <Section title="投递历史" description="死信可手动发起新一轮重试；过期或已撤销的业务凭据不会重新发送。" action={<label className="compact-select"><span>状态</span><select aria-label="邮件投递状态" value={status} onChange={(event) => setStatus(event.target.value as "ALL" | DeliveryStatus)}><option value="ALL">全部</option><option value="SENDING">发送中</option><option value="DELIVERED">已投递</option><option value="SUPPRESSED">已抑制</option><option value="DEAD_LETTER">死信</option></select></label>}>
      <div className="mail-counts"><div><strong>{state.data.counts.total}</strong><span>全部</span></div><div><strong>{state.data.counts.sending}</strong><span>发送中</span></div><div><strong>{state.data.counts.delivered}</strong><span>已投递</span></div><div><strong>{state.data.counts.suppressed}</strong><span>已抑制</span></div><div><strong>{state.data.counts.deadLetter}</strong><span>死信</span></div></div>
      {state.data.deliveries.length === 0 && <EmptyState icon={Mail} title="没有匹配的邮件记录" detail="触发邮箱验证、账号找回、组织邀请或导出完成通知后会显示在这里。" />}
      <div className="mail-delivery-list">{state.data.deliveries.map((delivery) => <article key={delivery.id}>
        <span className={`mail-state-mark ${delivery.status.toLowerCase()}`} />
        <div className="mail-delivery-main"><strong>{delivery.templateKey}</strong><span>{delivery.recipientHint} · {delivery.eventType}</span><small>开始 {formatDate(delivery.startedAt)} · 更新 {formatDate(delivery.updatedAt)}{delivery.status === "SENDING" && delivery.nextAttemptAt ? ` · 下次重试 ${formatDate(delivery.nextAttemptAt)}` : ""}</small></div>
        <div className="mail-delivery-result"><StatusBadge tone={delivery.status === "DELIVERED" ? "good" : delivery.status === "DEAD_LETTER" ? "danger" : delivery.status === "SENDING" ? "warning" : "neutral"}>{labels[delivery.status]}</StatusBadge><small>{delivery.lastErrorCode ?? `尝试 ${delivery.attemptCount} 次`}</small></div>
        {delivery.retryable && <button className="secondary-button mail-retry-button" type="button" aria-label={`重新投递 ${delivery.templateKey}`} disabled={retry.isPending} onClick={() => { if (window.confirm("重新投递这封死信邮件？仅在原业务凭据仍有效时才会发送。")) retry.mutate(delivery.id); }}><RotateCcw />重新投递</button>}
      </article>)}</div>
      <MutationMessage error={retry.error} success={retry.isSuccess && "死信已重新加入投递队列"} />
    </Section>}
  </>;
}
